import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator';
import PasswordInput from '../components/PasswordInput';
import TrovaBrand from '../components/Auth/TrovaBrand';

const ResetPasswordPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/\d/.test(password)) {
      return 'Password must contain at least one number';
    }
    if (!/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~]/.test(password)) {
      return 'Password must contain at least one special character (!@#$%^&*)';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to reset password');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/auth/login');
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <TrovaBrand />
            <h2 className="login-title">Password Reset Successful</h2>
            <p className="login-subtitle">
              Your password has been successfully reset.
            </p>
          </div>

          <div className="login-form-container">
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <svg
                style={{ width: '64px', height: '64px', color: 'var(--color-primary)', margin: '0 auto 16px' }}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p style={{ color: 'var(--color-text-tertiary)', marginBottom: '16px' }}>
                You will be redirected to the login page in a few seconds...
              </p>
            </div>

            <Link
              to="/auth/login"
              className="form-button"
              style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}
            >
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <TrovaBrand />
            <h2 className="login-title">Invalid Reset Link</h2>
            <p className="login-subtitle">
              This password reset link is invalid or has expired.
            </p>
          </div>

          <div className="login-form-container">
            <Link
              to="/forgot-password"
              className="form-button"
              style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}
            >
              Request New Reset Link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <Helmet>
        <title>Reset Password</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="login-container">
        <div className="login-header">
          <TrovaBrand />
          <h2 className="login-title">Reset Your Password</h2>
          <p className="login-subtitle">
            Enter your new password below.
          </p>
        </div>

        <div className="login-form-container">
          {error && (
            <div
              style={{
                backgroundColor: 'var(--color-error-bg)',
                border: '1px solid var(--color-error)',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '16px',
                color: 'var(--color-danger)',
                fontSize: '14px',
              }}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="newPassword" className="form-label">
                New Password
              </label>
              <PasswordInput
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
              />
              <PasswordStrengthIndicator password={newPassword} />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">
                Confirm Password
              </label>
              <PasswordInput
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
              />
            </div>

            <button type="submit" className="form-button" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>

          <div className="signup-prompt" style={{ marginTop: '24px' }}>
            <Link to="/auth/login">Back to Login</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
